// /settings (docs/08-app-design.md 5.11): RPC URL, Jupiter endpoint and
// key (stored sealed), auto-lock interval, display currency, venue
// exclusions, and "Forget this device". The adopted design (section 3) has
// one theme, so the dark/light control of the design doc is not built; see
// the M7.3 report. Saving persists with `saveSettings` and reloads the
// session so the running app picks up the change; an RPC change still only
// takes effect at the next unlock (session.ts opens the engine's Rpc once,
// at unlock), so "LOCK AND APPLY" saves and locks in one step. On a
// legacy device whose older fee reserve holds SOL, "This device" shows the
// older reserve notice and its move (docs/18-one-fee-reserve.md section 6).

import { useState } from 'react';
import { useLocation } from 'wouter';
import { PUBLIC_DEXES, sealJupiterKey } from '@thevault/sdk';
import { forgetConfirmDisabled, olderReserveClause, olderReserveForgetSentence, useLegacyReserve } from '../components/ForgetSheet';
import { FEE_RESERVE_DISCLOSURE } from '../components/GasTank';
import { OlderReserveNotice, useOlderReserve } from '../components/OlderReserve';
import { Button, Field, Sheet, StatusLine, Toast, useToast } from '../components/primitives';
import { Shell } from '../components/Shell';
import { describe } from '../engine';
import { session, useSession } from '../session';
import { type AppSettings, saveSettings } from '../storage';

// DEV-only test hook (app/e2e/settings-verify.spec.ts): exposes the
// session's in-memory Jupiter key so a flow test can prove it survives lock
// and unlock without ever reading it back from storage. Mirrors
// app/src/main.tsx's `__thevault_test`; compiled away in a production build.
if (import.meta.env.DEV) {
  (window as unknown as { __thevault_test_jupiter?: () => string | null }).__thevault_test_jupiter = () => session.jupiterApiKey();
}

function connectSrcHosts(): string[] {
  const meta = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
  const content = meta?.getAttribute('content') ?? '';
  const match = /connect-src\s+([^;]*)/.exec(content);
  if (!match) return [];
  return match[1]!.trim().split(/\s+/);
}

function originAllowed(url: string, hosts: string[]): boolean {
  try {
    return hosts.includes(new URL(url).origin);
  } catch {
    return false;
  }
}

function clampMinutes(n: number): number {
  if (!Number.isFinite(n)) return 10;
  return Math.min(60, Math.max(1, Math.round(n)));
}

export function Settings() {
  const { settings } = useSession();
  const [, navigate] = useLocation();
  const [toast, say] = useToast();

  const [rpcUrl, setRpcUrl] = useState(settings.rpcUrl);
  const [jupiterUrl, setJupiterUrl] = useState(settings.jupiterUrl);
  const [autoLockMinutes, setAutoLockMinutes] = useState(settings.autoLockMinutes);
  const [displayCurrency, setDisplayCurrency] = useState<AppSettings['displayCurrency']>(settings.displayCurrency);
  const [excludedVenues, setExcludedVenues] = useState<string[]>(settings.excludedVenues);
  // Session-only (R42): read from and written to the session, never stored.
  const { development } = useSession();

  const [keyDraft, setKeyDraft] = useState('');
  const [keySheetOpen, setKeySheetOpen] = useState(false);
  const [keyPassphrase, setKeyPassphrase] = useState('');
  const [keyError, setKeyError] = useState<string | null>(null);
  const [keyBusy, setKeyBusy] = useState(false);

  const [forget, setForget] = useState(false);
  // A legacy device still holds the per-device key of an earlier release, which Forget deletes (docs/18 section 3);
  // a failed read fails closed ('unknown': the device may hold one).
  const legacy = useLegacyReserve();
  const [forgetWord, setForgetWord] = useState('');
  // The older reserve itself, read at every visit (docs/18 section 6): the notice shows while it holds SOL.
  const engine = session.engine();
  const older = useOlderReserve(engine);

  const hosts = connectSrcHosts();
  const rpcAllowed = originAllowed(rpcUrl, hosts);

  const buildSettings = (overrides: Partial<AppSettings> = {}): AppSettings => ({
    ...settings,
    rpcUrl,
    jupiterUrl,
    autoLockMinutes: clampMinutes(autoLockMinutes),
    displayCurrency,
    excludedVenues,
    ...overrides,
  });

  const persist = async (overrides: Partial<AppSettings> = {}): Promise<AppSettings> => {
    const next = buildSettings(overrides);
    await saveSettings(next);
    await session.reloadSettings();
    return next;
  };

  const doSave = async () => {
    const next = await persist();
    setAutoLockMinutes(next.autoLockMinutes);
    say('Saved');
  };

  const doLockAndApply = async () => {
    await persist();
    // published at once (session.ts, R68): the door opens now; unlock waits for the teardown itself
    void session.lock();
    navigate('/locked');
  };

  const toggleVenue = (venue: string, excluded: boolean) => {
    setExcludedVenues((prev) => (excluded ? (prev.includes(venue) ? prev : [...prev, venue]) : prev.filter((v) => v !== venue)));
  };

  const openKeySheet = () => {
    if (!keyDraft) return;
    setKeyError(null);
    setKeyPassphrase('');
    setKeySheetOpen(true);
  };

  const confirmKey = async () => {
    if (!keyPassphrase || keyBusy) return;
    setKeyBusy(true);
    setKeyError(null);
    try {
      const sealed = await sealJupiterKey(keyDraft, keyPassphrase);
      await persist({ jupiterApiKeySealed: sealed });
      session.adoptJupiterApiKey(keyDraft);
      setKeySheetOpen(false);
      setKeyDraft('');
      setKeyPassphrase('');
      say('Saved');
    } catch (e) {
      setKeyError(describe(e));
    } finally {
      setKeyBusy(false);
    }
  };

  const clearKey = async () => {
    await persist({ jupiterApiKeySealed: undefined });
    session.adoptJupiterApiKey(null);
    say('Key cleared');
  };

  const doForget = async () => {
    if (await session.forget()) navigate('/welcome');
  };

  return (
    <Shell number="06" title="Settings" detail="APPLIES AT THE NEXT UNLOCK">
      <h1>Settings.</h1>

      <section className="panel" aria-labelledby="settings-network-title">
        <div className="console-heading">
          <h3 id="settings-network-title">Network</h3>
        </div>
        <Field id="rpc-url" label="RPC URL" help="An RPC change takes effect at the next unlock. The keyless default reads the vault and sends actions but does not list token accounts; an endpoint with your own key, such as Helius, lists them too.">
          <input id="rpc-url" className="input" value={rpcUrl} onChange={(e) => setRpcUrl(e.target.value)} autoComplete="off" spellCheck={false} />
        </Field>
        {!rpcAllowed ? (
          <StatusLine state="failed" word="Not reachable from this build.">
            This build only allows these hosts: {hosts.length ? hosts.join(', ') : 'none configured'}.
          </StatusLine>
        ) : null}
        <label className="checkbox-row">
          <input type="checkbox" checked={development} onChange={(e) => session.setDevelopment(e.target.checked)} />
          <span>Development session: sign even if the deployed program's hash differs from the published one</span>
        </label>
        <p>Leave this off. The app reads the deployed program's hash from the RPC endpoint before every action that signs and refuses to sign when it differs from the published one; Verify shows the same comparison. This switch is not saved: it is off again after a lock or a reload, and the frame shows a warning while it is on.</p>
        <div className="actions">
          <Button variant="ghost" small onClick={() => void doLockAndApply()}>
            Lock and apply
          </Button>
        </div>
      </section>

      <section className="panel" aria-labelledby="settings-jupiter-title">
        <div className="console-heading">
          <h3 id="settings-jupiter-title">Jupiter</h3>
        </div>
        <Field id="jupiter-url" label="Jupiter endpoint URL">
          <input id="jupiter-url" className="input" value={jupiterUrl} onChange={(e) => setJupiterUrl(e.target.value)} autoComplete="off" spellCheck={false} />
        </Field>
        <Field id="jupiter-key" label="Jupiter API key" help="Stored sealed under the device passphrase. Leave this blank to keep the current key.">
          <input id="jupiter-key" className="input" type="password" value={keyDraft} onChange={(e) => setKeyDraft(e.target.value)} autoComplete="off" />
        </Field>
        <p className="ink">{settings.jupiterApiKeySealed ? 'A key is stored, sealed on this device.' : 'No key is stored.'}</p>
        <div className="actions">
          <Button variant="ghost" small disabled={!keyDraft} onClick={openKeySheet}>
            Set key
          </Button>
          <Button variant="danger" small disabled={!settings.jupiterApiKeySealed} onClick={() => void clearKey()}>
            Clear key
          </Button>
        </div>
      </section>

      <section className="panel" aria-labelledby="settings-autolock-title">
        <div className="console-heading">
          <h3 id="settings-autolock-title">Auto-lock</h3>
        </div>
        <Field id="autolock" label="Auto-lock, in minutes" help="From 1 to 60 minutes without interaction.">
          <input id="autolock" className="input" type="number" min={1} max={60} value={autoLockMinutes} onChange={(e) => setAutoLockMinutes(Number(e.target.value))} />
        </Field>
      </section>

      <section className="panel" aria-labelledby="settings-display-title">
        <div className="console-heading">
          <h3 id="settings-display-title">Display</h3>
        </div>
        <Field id="display-currency" label="Display currency">
          <div className="radio-row">
            <label className="radio">
              <input type="radio" name="display-currency" checked={displayCurrency === 'raw'} onChange={() => setDisplayCurrency('raw')} />
              <span>Raw units</span>
            </label>
            <label className="radio">
              <input type="radio" name="display-currency" checked={displayCurrency === 'usd'} onChange={() => setDisplayCurrency('usd')} />
              <span>Indicative USD hint</span>
            </label>
          </div>
        </Field>
        <p>Raw units are always available on tap, even with the USD hint on.</p>
      </section>

      <section className="panel" aria-labelledby="settings-venues-title">
        <div className="console-heading">
          <h3 id="settings-venues-title">Venue exclusions</h3>
        </div>
        <p>A checked venue is excluded from the quote for swaps.</p>
        <div className="checkbox-list">
          {PUBLIC_DEXES.map((venue) => (
            <label key={venue} className="checkbox-row">
              <input type="checkbox" checked={excludedVenues.includes(venue)} onChange={(e) => toggleVenue(venue, e.target.checked)} />
              <span>{venue}</span>
            </label>
          ))}
        </div>
      </section>

      <div className="actions">
        <Button onClick={() => void doSave()}>
          Save
        </Button>
      </div>

      <section className="panel" aria-labelledby="settings-device-title">
        <div className="console-heading">
          <h3 id="settings-device-title">This device</h3>
        </div>
        <p className="ink">
          Forget this device to delete its sealed key{olderReserveClause(legacy)} and its ledger. The vault and its fee reserve are not touched: the phrase opens both anywhere.
        </p>
        {/* the fee reserve is derived from the words (docs/18 section 3); the compromise disclosure, section 7 */}
        <p className="ink">Every device that opens this vault uses the same fee reserve, and your 24 words restore it. {FEE_RESERVE_DISCLOSURE}</p>
        <OlderReserveNotice engine={engine} older={older} />
        <div className="actions">
          <Button variant="danger" small onClick={() => setForget(true)}>
            Forget this device
          </Button>
        </div>
      </section>

      <Sheet open={keySheetOpen} onClose={() => setKeySheetOpen(false)} title="Save the Jupiter key">
        <h2>Confirm the device passphrase.</h2>
        <p>The Jupiter API key is sealed under the device passphrase, the same way the key material is.</p>
        <Field id="key-passphrase" label="Device passphrase">
          <input id="key-passphrase" className="input" type="password" autoComplete="current-password" value={keyPassphrase} onChange={(e) => setKeyPassphrase(e.target.value)} />
        </Field>
        {keyError ? (
          <StatusLine state="failed" word="Not saved.">
            {keyError}
          </StatusLine>
        ) : null}
        <div className="actions">
          <Button disabled={!keyPassphrase || keyBusy} working={keyBusy} onClick={() => void confirmKey()}>
            Confirm
          </Button>
          <Button variant="ghost" onClick={() => setKeySheetOpen(false)}>
            Cancel
          </Button>
        </div>
      </Sheet>

      <Sheet open={forget} onClose={() => setForget(false)} title="Forget this device">
        <h2>Forget this device.</h2>
        <p>
          This deletes the app&rsquo;s data on this device only. Your fee reserve is not removed: your 24 words open it on any device.{legacy === false ? '' : ` ${olderReserveForgetSentence(legacy)}`}
        </p>
        <Field id="forget" label="Type FORGET to confirm">
          <input id="forget" className="input" value={forgetWord} onChange={(e) => setForgetWord(e.target.value)} autoComplete="off" />
        </Field>
        <div className="actions">
          <Button variant="danger" disabled={forgetConfirmDisabled(forgetWord, legacy)} onClick={() => void doForget()}>
            Forget this device
          </Button>
          <Button variant="ghost" onClick={() => setForget(false)}>
            Keep it
          </Button>
        </div>
      </Sheet>
      <Toast message={toast} />
    </Shell>
  );
}
